import { SettlementDetail } from '../../../models/Entities/SettlementDetails/SettlementDetail';
import { ProtocolConsumptionRealizeTypeEnum } from '../../../models/Enums/ProtocolConsumptionRealizeTypeEnum';
import { PatientVisitStatusEnum } from '../../../models/Enums/PatientVisitStatusEnum';
import { feeBreakdown } from '../../../commons/fee/PractitionerFee.Logic';

/** Tabla de una liquidación a médico: agrupación y subtotales, sin React. */

export interface PractitionerDetailGroup {
    key: string;
    label: string;
    protocolName: string;
    stageOrder: number;
    items: SettlementDetail[];
}

export type ProtocolTitleLookup = (protocolId: number) => string | undefined;

const noTitle: ProtocolTitleLookup = () => undefined;

/**
 * El título del protocolo sale del store; el `protocolName` que manda el API
 * es el número de protocolo y queda sólo como respaldo.
 */
const protocolNameOf = (detail: SettlementDetail, titleOf: ProtocolTitleLookup): string =>
    (detail.protocolId ? (titleOf(detail.protocolId) || '').trim() : '')
    || (detail.protocolName || '').trim()
    || (detail.protocolId ? `Protocolo #${detail.protocolId}` : '');

/**
 * Una liquidación a médico junta visitas de varios protocolos. Se agrupa por
 * visita (etapa) como siempre, y cuando hay más de un protocolo la etiqueta
 * lleva el protocolo adelante para que dos "Visita 1" no se confundan.
 */
export const groupPractitionerDetails = (
    details: SettlementDetail[] | null | undefined,
    titleOf: ProtocolTitleLookup = noTitle
): PractitionerDetailGroup[] => {
    const list = details ?? [];
    const protocols = new Set(list.map((detail) => protocolNameOf(detail, titleOf)).filter(Boolean));
    const multiProtocol = protocols.size > 1;
    const groups = new Map<string, PractitionerDetailGroup>();

    list.forEach((detail) => {
        const protocolName = protocolNameOf(detail, titleOf);
        const stageName = (detail.contractStageName || '').trim() || 'Sin etapa';
        const key = `${protocolName}|${stageName}`;
        const existing = groups.get(key);
        if (existing) {
            existing.items.push(detail);
            return;
        }
        groups.set(key, {
            key,
            label: multiProtocol && protocolName ? `${protocolName} · ${stageName}` : stageName,
            protocolName,
            stageOrder: detail.contractStage ?? 0,
            items: [detail],
        });
    });

    return Array.from(groups.values()).sort((a, b) =>
        a.protocolName.localeCompare(b.protocolName, undefined, { sensitivity: 'base', numeric: true })
        || (a.stageOrder - b.stageOrder)
        || a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true })
    );
};

export interface PractitionerSubtotal {
    currency: string;
    qty: number;
    base: number;
    fee: number;
}

/** Cantidad, base de cálculo y honorario por moneda. */
export const practitionerSubtotals = (details: SettlementDetail[]): PractitionerSubtotal[] => {
    const totals = new Map<string, PractitionerSubtotal>();
    details.forEach((detail) => {
        const currency = (detail.currency || 'ARS').toUpperCase();
        const current = totals.get(currency) ?? { currency, qty: 0, base: 0, fee: 0 };
        current.qty += detail.quantityToSettle || 0;
        current.base += feeBreakdown(detail).base;
        current.fee += detail.amount || 0;
        totals.set(currency, current);
    });
    return Array.from(totals.values()).sort((a, b) => a.currency.localeCompare(b.currency));
};

const REALIZE_LABELS: Record<number, string> = {
    [ProtocolConsumptionRealizeTypeEnum.RealizedRemotely]: 'Remota',
    [ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace]: 'Presencial',
    [ProtocolConsumptionRealizeTypeEnum.RealizedAtHome]: 'Domicilio',
    [ProtocolConsumptionRealizeTypeEnum.NotRealized]: 'No realizada',
};

/** Estado de la visita en la historia clínica (EMR). Mismas etiquetas que la lista de consumos del médico. */
const VISIT_STATUS: Record<number, { label: string; variant: string }> = {
    [PatientVisitStatusEnum.Started]:       { label: 'Iniciada', variant: 'started' },
    [PatientVisitStatusEnum.Completed]:     { label: 'Completada', variant: 'completed' },
    [PatientVisitStatusEnum.Skipped]:       { label: 'Salteada', variant: 'skipped' },
    [PatientVisitStatusEnum.Failed]:        { label: 'Falla de selección', variant: 'failed' },
    [PatientVisitStatusEnum.Changed]:       { label: 'Modificada', variant: 'changed' },
    [PatientVisitStatusEnum.AutoCompleted]: { label: 'Autocompletada', variant: 'autocompleted' },
};

export const visitStatusOf = (status: number | undefined | null): { label: string; variant: string } | undefined =>
    status === undefined || status === null ? undefined : VISIT_STATUS[status];

/** Modalidad de la visita: el enum si vino, si no la etiqueta libre que se usaba antes. */
export const realizeLabel = (detail: Pick<SettlementDetail, 'realizedType' | 'tag1'>): string =>
    (detail.realizedType !== undefined && REALIZE_LABELS[detail.realizedType]) || (detail.tag1 || '').trim() || '—';
